import { ImageViewer } from '@core/components';
import type { GraphQLAuditItem } from '@internal-audit/domain/graphql-types';
import { Text, TextArea } from '@soteria/design-system-ve';
import { useState } from 'react';

import * as imageStyles from '../../AuditItem/AuditItemImage.css';
import { LikeDislikeButtons } from '../../LikeDislikeButtons/LikeDislikeButtons';
import * as styles from '../AuditDecisionPanel.css';
import { useOcrImage } from '../hooks/useOcrImage';
import * as spacingStyles from '../spacing.css';

interface OcrSectionProps {
  selectedItem: GraphQLAuditItem | null;
  ocrCorrect: boolean | null;
  correctedOcr: string;
  isSessionCompleted: boolean;
  onOcrCorrectChange: (value: boolean | null) => void;
  onCorrectedOcrChange: (value: string) => void;
  getOcrText: (item: GraphQLAuditItem | null) => string;
}

/**
 * Image thumbnail component - matches the main audit item card styling
 */
function ImageThumbnail({
  imageUrl,
  altText,
  isSafe,
  isContentBlurred,
  onClick,
}: {
  imageUrl: string;
  altText: string;
  isSafe: boolean;
  isContentBlurred: boolean;
  onClick: () => void;
}) {
  const [imageError, setImageError] = useState(false);
  const applyBlur = !isSafe && isContentBlurred;

  return (
    <button aria-label="View full-size content" className={imageStyles.thumbnailContainer} type="button" onClick={onClick}>
      {imageError ? (
        <Text className={imageStyles.thumbnailErrorText}>Image unavailable</Text>
      ) : (
        <img
          alt={altText}
          className={`${imageStyles.thumbnailImage} ${applyBlur ? imageStyles.blurredThumbnail : ''}`}
          src={imageUrl}
          onError={() => setImageError(true)}
        />
      )}
    </button>
  );
}

export function OcrSection({
  selectedItem,
  ocrCorrect,
  correctedOcr,
  isSessionCompleted,
  onOcrCorrectChange,
  onCorrectedOcrChange,
  getOcrText,
}: OcrSectionProps) {
  // Image viewer state
  const [isImageViewerOpen, setIsImageViewerOpen] = useState(false);
  // Blur state for unsafe content - starts blurred
  const [isContentBlurred, setIsContentBlurred] = useState(true);

  // Image logic using custom hook to reduce complexity
  const { imageUrl, shouldShowImage, isSafeContent } = useOcrImage(selectedItem);

  const handleChange = (value: boolean) => {
    // If clicking the same button that's already selected, deselect it (set to null)
    const newValue = ocrCorrect === value ? null : value;

    onOcrCorrectChange(newValue);
  };

  const handleImageClick = () => {
    setIsImageViewerOpen(true);
  };

  const handleToggleBlur = () => {
    setIsContentBlurred(prev => !prev);
  };

  return (
    <div className={spacingStyles.sectionContainer}>
      <Text className={styles.sectionTitle} id="ocr-label">
        OCR (Optical Character Recognition)
      </Text>

      <div className={styles.controlRow}>
        <span className={styles.assessmentQuestion}>Do you agree with the OCR text?</span>
        <LikeDislikeButtons disabled={isSessionCompleted} name="ocr" value={ocrCorrect} onChange={handleChange} />
      </div>

      <div className={styles.transcriptionPreview}>
        <span className={styles.label}>Original:</span>
        <p className={styles.originalText}>{getOcrText(selectedItem) || 'No OCR text available'}</p>

        {/* Image Thumbnail - Matches main audit item card styling with blur support */}
        {shouldShowImage && imageUrl && selectedItem && (
          <div className={styles.imageThumbnailWrapper}>
            <ImageThumbnail
              altText={`Audit item ${selectedItem.id}`}
              imageUrl={imageUrl}
              isContentBlurred={isContentBlurred}
              isSafe={isSafeContent}
              onClick={handleImageClick}
            />
          </div>
        )}
      </div>

      {ocrCorrect === false && (
        <div className={styles.transcriptionCorrectionField}>
          <TextArea
            disabled={isSessionCompleted}
            placeholder="Enter the corrected OCR text..."
            rows={4}
            title="Corrected OCR Text"
            value={correctedOcr}
            onChange={e => onCorrectedOcrChange(e.target.value)}
          />
        </div>
      )}

      {/* Full-Size Image Viewer with blur toggle */}
      {shouldShowImage && imageUrl && (
        <ImageViewer
          altText={`Audit item ${selectedItem?.id}`}
          imageUrl={imageUrl}
          isBlurred={isContentBlurred}
          isOpen={isImageViewerOpen}
          isSafe={isSafeContent}
          title="Audit Content"
          onClose={() => setIsImageViewerOpen(false)}
          onToggleBlur={handleToggleBlur}
        />
      )}
    </div>
  );
}
